// src/pages/HousingNavigatorAnalytics.js
// Housing Navigator Massachusetts' AI Search Mode dashboard (login "housingnavigator").
// Anonymized, aggregated counts only, from GET /hn/dashboard on the chatbot server
// (server/housingnavigator/dashboard.js): conversations on HNMI's own sites,
// counted from the pilot launch, plus the listing searches behind them.
// Same look as the housing authority dashboard (DashboardPage.js): white
// shadow-lg cards, tinted icon chips, blue day bars, its chart colours.
import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, RefreshCw, MessagesSquare, MessageSquareText, Search, SearchX, MousePointerClick, ThumbsUp,
  Clock, Mic, Globe, Accessibility, MapPin, LifeBuoy, HelpCircle, Gauge, Info, User, BedDouble,
  Filter, CalendarDays, Monitor, Volume2, Funnel
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

// DashboardPage chart colours, in the same order.
const PALETTE = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];
const GOOD = '#10B981';
const BAD = '#EF4444';
// Tailwind 100/600 pairs, as the housing authority stat cards use.
const CHIP = {
  blue: ['#DBEAFE', '#2563EB'], green: ['#D1FAE5', '#059669'], purple: ['#EDE9FE', '#7C3AED'], orange: ['#FFEDD5', '#EA580C'],
  red: ['#FEE2E2', '#DC2626'], cyan: ['#CFFAFE', '#0891B2'], indigo: ['#E0E7FF', '#4F46E5'], pink: ['#FCE7F3', '#DB2777']
};

const LANGS = { en: 'English', es: 'Spanish', pt: 'Portuguese', zh: 'Chinese', fr: 'French', ht: 'Haitian Creole', it: 'Italian', ru: 'Russian', vi: 'Vietnamese', el: 'Greek', ar: 'Arabic' };
const BEDS = { SRO: 'SRO', STUDIO: 'Studio', ONE: '1 BR', TWO: '2 BR', THREE: '3 BR', FOUR_PLUS: '4+ BR' };
const BED_ORDER = ['SRO', 'STUDIO', 'ONE', 'TWO', 'THREE', 'FOUR_PLUS'];
const ACCESS = { MOBILITY: 'Mobility', HEARING: 'Hearing', VISION: 'Vision' };
const AVAIL = { fcfs: 'Available now (first come, first served)', lottery: 'Lotteries', shortWaitlist: 'Short waitlist', waitlistOpen: 'Open waitlist' };
const NOT_A_FILTER = { transit: 'Near transit', schools: 'Schools / school district', utilities: 'Utilities included', built: 'Newer buildings' };
const MODES = { exact: 'Matches in the place asked', nearby: 'Showed nearby towns', relaxed: 'Showed the place with fewer filters', unknown: 'No match anywhere close' };
const CLICKS = { click_listing: 'Opened a listing', click_see_all: 'Opened "See all on Housing Navigator"', click_call: 'Tapped a phone number', click_link: 'Opened another link', read_aloud: 'Played an answer aloud' };
const CRISIS = { tonight: 'Nowhere to stay tonight', eviction: 'Eviction', dv: 'Domestic or family violence', safety: 'Safety threat', selfharm: 'Thoughts of self-harm' };
const TOPICS = { property: 'Listings and properties', waitlist: 'Waitlists and lotteries', eligibility: 'Eligibility and income', application: 'Applying', other: 'Other questions' };
const DEVICES = { desktop: 'Desktop', mobile: 'Mobile', tablet: 'Tablet' };
const DAYS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const label = (map, k) => map[k] || (k ? String(k).replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : 'Unknown');
const fmtDay = d => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtNum = n => (n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n || 0));
const withColors = (rows, map, colors = PALETTE) => (rows || []).map((r, i) => ({ ...r, label: map ? label(map, r.name) : r.name, color: colors[i % colors.length] }));

const Nothing = ({ text = 'No data yet' }) => <p className="text-gray-500 text-center py-6">{text}</p>;

function StatCard({ icon: Icon, title, value, subtitle, color = 'blue' }) {
  const [bg, fg] = CHIP[color];
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-lg" style={{ background: bg }}><Icon className="h-6 w-6" style={{ color: fg }} aria-hidden="true" /></div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm text-gray-600 mb-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

function Panel({ title, icon: Icon, note, children, className = '', delay = 0.4 }) {
  return (
    <motion.section initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay }}
      className={`bg-white rounded-xl p-6 shadow-lg ${className}`} aria-label={title}>
      <h3 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-blue-600" aria-hidden="true" />}{title}
      </h3>
      {note ? <p className="text-sm text-gray-500 mb-4">{note}</p> : <div className="mb-4" />}
      {children}
    </motion.section>
  );
}

/** Donut with a legend (count + share), like the language pie on the housing authority dashboard. */
function Donut({ rows, centerValue, centerLabel, empty, wide }) {
  const total = (rows || []).reduce((n, r) => n + r.count, 0);
  if (!total) return <Nothing text={empty} />;
  // Slices are filled ring wedges, not dashed circle strokes: a 1% dash on a
  // 30px stroke renders as a bent, jagged sliver in Chrome.
  const OUT = 85, IN = 55;
  const pt = (rad, a) => `${(100 + rad * Math.sin(a)).toFixed(3)} ${(100 - rad * Math.cos(a)).toFixed(3)}`;
  const shown = rows.filter(r => r.count > 0);
  let start = 0;
  return (
    <div className={`flex flex-col items-center justify-center gap-6 ${wide ? 'sm:flex-row' : ''}`}>
      <svg viewBox="0 0 200 200" className="w-40 h-40 flex-shrink-0" role="img"
        aria-label={rows.map(r => `${r.label}: ${r.count} (${pct(r.count, total)}%)`).join(', ')}>
        {shown.length === 1
          ? <circle cx="100" cy="100" r={(OUT + IN) / 2} fill="none" stroke={shown[0].color} strokeWidth={OUT - IN}><title>{`${shown[0].label}: ${shown[0].count}`}</title></circle>
          : shown.map(r => {
            const a0 = start, a1 = start + (r.count / total) * 2 * Math.PI, big = a1 - a0 > Math.PI ? 1 : 0;
            start = a1;
            return <path key={r.label} fill={r.color} stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"
              d={`M ${pt(OUT, a0)} A ${OUT} ${OUT} 0 ${big} 1 ${pt(OUT, a1)} L ${pt(IN, a1)} A ${IN} ${IN} 0 ${big} 0 ${pt(IN, a0)} Z`}>
              <title>{`${r.label}: ${r.count}`}</title></path>;
          })}
        <text x="100" y="98" textAnchor="middle" className="fill-gray-900" style={{ fontSize: 28, fontWeight: 700 }}>{centerValue ?? total}</text>
        <text x="100" y="120" textAnchor="middle" className="fill-gray-500" style={{ fontSize: 12 }}>{centerLabel}</text>
      </svg>
      <ul className={`space-y-2 min-w-0 ${wide ? '' : 'w-full'}`}>
        {rows.map(r => (
          <li key={r.label} className="flex items-center gap-3">
            <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: r.color }} aria-hidden="true" />
            <span className="text-sm font-medium text-gray-700 flex-1">{r.label}</span>
            <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">{r.count} · {pct(r.count, total)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal bars. With `split`, each bar shows the part (red) inside the whole (blue). */
function HBars({ rows, empty, split, splitLabel, color }) {
  if (!rows || !rows.length) return <Nothing text={empty} />;
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <ul className="space-y-3">
      {rows.map((r, i) => {
        const part = split ? r[split] || 0 : 0;
        return (
          <li key={r.label}>
            <div className="flex justify-between text-sm mb-1 gap-2">
              <span className="font-medium text-gray-700">{r.label}</span>
              <span className="text-gray-600 tabular-nums whitespace-nowrap">
                {split && part > 0 && part >= r.count
                  ? `${r.count} ${r.count === 1 ? 'search' : 'searches'} · 0 matches`   // every search here came back empty
                  : `${r.count}${split && part > 0 ? ` · ${part} ${splitLabel}` : ''}`}
              </span>
            </div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex" aria-hidden="true">
              <div className="h-full" style={{ width: `${((r.count - part) / max) * 100}%`, background: color || (split ? '#3B82F6' : r.color || PALETTE[i % PALETTE.length]) }} />
              {part > 0 && <div className="h-full" style={{ width: `${(part / max) * 100}%`, background: BAD }} />}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Day bars in the housing authority style (blue-400, latest day blue-600, value on top, hover tooltip).
 *  With `zeroKey`, each bar is split: matched (green) under 0 matches (red). */
function DayBars({ rows, unit, zeroKey }) {
  if (!rows || !rows.length) return <Nothing />;
  const max = Math.max(...rows.map(r => r.count), 1);
  const every = Math.ceil(rows.length / 10);
  return (
    <div>
      <div className="relative h-64 pt-6" role="img" aria-label={rows.map(r => `${fmtDay(r.day)}: ${r.count} ${unit}${zeroKey ? `, ${r[zeroKey] || 0} with 0 matches` : ''}`).join('; ')}>
        <div className="flex items-end gap-1 h-full">
          {rows.map((r, i) => {
            const h = Math.max((r.count / max) * 100, 3);
            const zero = zeroKey ? r[zeroKey] || 0 : 0;
            const last = i === rows.length - 1;
            return (
              <div key={r.day} className="relative flex-1 h-full flex flex-col justify-end group min-w-0">
                {rows.length <= 31 && <div className="text-[10px] font-medium text-gray-700 text-center mb-0.5">{r.count}</div>}
                <div className="w-full rounded-t overflow-hidden flex flex-col justify-end transition-opacity hover:opacity-80" style={{ height: `${h}%` }}>
                  {zeroKey ? (
                    <>
                      <div style={{ height: `${pct(zero, r.count)}%`, background: BAD }} />
                      <div className="flex-1" style={{ background: GOOD }} />
                    </>
                  ) : <div className={`flex-1 ${last ? 'bg-blue-600' : 'bg-blue-400'}`} />}
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800/80 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10"
                  style={{ bottom: `calc(${h}% + 22px)` }}>
                  {fmtDay(r.day)}<br />{r.count} {unit}{zeroKey ? <><br />{zero} with 0 matches</> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1 mt-1">
        {rows.map((r, i) => <div key={r.day} className="flex-1 text-[10px] text-gray-600 text-center min-w-0">{i % every === 0 ? fmtDay(r.day) : ''}</div>)}
      </div>
      {zeroKey && (
        <div className="flex justify-center gap-6 text-sm text-gray-600 mt-3">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: GOOD }} />Found matches</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: BAD }} />0 matches</span>
        </div>
      )}
    </div>
  );
}

/** Vertical columns for categories (hours, weekdays, unit sizes). */
function Columns({ rows, color = '#3B82F6', top: topColor = '#2563EB', height = 'h-48' }) {
  const total = (rows || []).reduce((n, r) => n + r.count, 0);
  if (!total) return <Nothing />;
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div>
      <div className={`flex items-end gap-1 ${height}`} role="img" aria-label={rows.map(r => `${r.label}: ${r.count}`).join(', ')}>
        {rows.map(r => {
          const top = r.count === max;
          return (
            <div key={r.label} className="flex-1 h-full flex flex-col justify-end min-w-0" title={`${r.label}: ${r.count}`}>
              {r.count > 0 && <div className="text-[10px] font-medium text-gray-700 text-center">{r.count}</div>}
              <div className="rounded-t" style={{ height: `${(r.count / max) * 100}%`, background: top ? topColor : color, opacity: top ? 1 : 0.75 }} />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1">
        {rows.map(r => <div key={r.label} className="flex-1 text-[10px] text-gray-600 text-center min-w-0 truncate">{r.short ?? r.label}</div>)}
      </div>
    </div>
  );
}

/** Half-circle gauge for the conversation limit. The period comes from the API:
 *  launch (Oct 30 2026) to Dec 1 is one period, then calendar months. */
function UsageGauge({ used, cap, from, resetsOn }) {
  const share = Math.min(used / (cap || 1), 1);
  const R = 80, L = Math.PI * R;
  const color = share >= 1 ? BAD : share >= 0.8 ? '#F59E0B' : '#3B82F6';
  const period = from && resetsOn ? `since ${fmtDay(from)}` : 'this month';
  const reset = resetsOn ? `Resets ${fmtDay(resetsOn)}.` : 'Resets on the 1st.';
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 115" className="w-full max-w-xs" role="img" aria-label={`${used} of ${cap} conversations used ${period}. ${reset}`}>
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="#E5E7EB" strokeWidth="18" strokeLinecap="round" />
        <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke={color} strokeWidth="18" strokeLinecap="round" strokeDasharray={`${share * L} ${L}`} />
        <text x="100" y="88" textAnchor="middle" className="fill-gray-900" style={{ fontSize: 30, fontWeight: 700 }}>{used}</text>
        <text x="100" y="108" textAnchor="middle" className="fill-gray-500" style={{ fontSize: 12 }}>of {cap} {period}</text>
      </svg>
      <p className="text-sm text-gray-600 mt-2">{pct(used, cap)}% used · {Math.max(cap - used, 0)} left</p>
      <p className="text-xs text-gray-500 mt-1 text-center">An email goes out at 80% and at the limit. {reset}</p>
    </div>
  );
}

/** Conversation funnel: each step as a share of all conversations. */
function FunnelChart({ steps }) {
  const first = steps[0]?.count || 0;
  if (!first) return <Nothing />;
  return (
    <ul className="space-y-3">
      {steps.map((s, i) => (
        <li key={s.label}>
          <div className="flex justify-between text-sm mb-1"><span className="font-medium text-gray-700">{s.label}</span><span className="text-gray-600 tabular-nums">{s.count} · {pct(s.count, first)}%</span></div>
          <div className="h-8 bg-gray-100 rounded-lg overflow-hidden" aria-hidden="true">
            <div className="h-full rounded-lg" style={{ width: `${pct(s.count, first)}%`, background: PALETTE[i] }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function HousingNavigatorAnalytics({ apiUrl }) {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (days) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${apiUrl}/hn/dashboard?days=${days}`);
      if (!r.ok) throw new Error(String(r.status));
      setData(await r.json());
    } catch (e) {
      setError('The dashboard could not be loaded. Please try again in a moment.');
    } finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { load(period); }, [load, period]);

  const c = data?.conversations || {};
  const s = data?.searches || {};
  const clicks = data?.engagement || [];
  const click = name => clicks.find(x => x.name === name) || { count: 0, conversations: 0 };
  const listingClicks = click('click_listing').count + click('click_see_all').count;
  const voice = (data?.inputMethods || []).find(x => x.name === 'voice')?.count || 0;
  const questions = (data?.inputMethods || []).reduce((n, x) => n + x.count, 0);
  const ratings = data?.ratings || { up: 0, down: 0 };
  const help = data?.helpCenter || {};
  const filters = [
    { name: 'Seniors / age-restricted', count: s.seniors || 0 }, { name: 'A budget', count: s.budget || 0 },
    { name: 'Rent based on income', count: s.rent_based_on_income || 0 }, { name: 'Fixed below-market rent', count: s.fixed_below_market || 0 },
    { name: 'Pet friendly', count: s.pets || 0 }
  ].filter(x => x.count > 0).sort((a, b) => b.count - a.count);
  const hours = Array.from({ length: 24 }, (_, h) => ({
    label: `${(h % 12) || 12}${h < 12 ? 'am' : 'pm'}`, short: h % 3 === 0 ? `${(h % 12) || 12}${h < 12 ? 'a' : 'p'}` : '',
    count: (data?.hours || []).find(x => x.hour === h)?.count || 0
  }));
  const weekdays = DAYS.slice(1).map((d, i) => ({ label: d, count: (data?.weekdays || []).find(x => x.day === i + 1)?.count || 0 }));
  const beds = BED_ORDER.map(k => ({ label: BEDS[k], count: (s.bedrooms || []).find(x => x.name === k)?.count || 0 }));
  const funnel = [
    { label: 'Conversations', count: c.total || 0 },
    { label: 'Searched for listings', count: data?.conversationsWithSearch || 0 },
    { label: 'Opened a listing', count: click('click_listing').conversations || 0 }
  ];
  const empty = !loading && data && !c.total;

  return (
    <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition} className="min-h-screen pt-20 bg-gray-50">
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">

            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-center mb-8">
              <div className="flex justify-center mb-6"><BarChart3 className="h-16 w-16 text-blue-600" aria-hidden="true" /></div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">AI Search Mode Dashboard</h1>
              <p className="text-xl text-gray-600">Housing Navigator Massachusetts: how renters use AI Search Mode</p>
            </motion.div>

            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  <h2 className="text-xl font-bold text-gray-900">Welcome back, Housing Navigator!</h2>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex gap-2">
                    {[7, 30, 90].map(d => (
                      <button key={d} type="button" onClick={() => setPeriod(d)} aria-pressed={period === d}
                        className={`px-3 py-1 rounded ${period === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{d}d</button>
                    ))}
                  </div>
                  <button type="button" onClick={() => load(period)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4 flex gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" aria-hidden="true" />
                <span>Anonymized, aggregated counts only: no conversation text is shown or kept for this dashboard. Counts include conversations on housingnavigatorma.org and search.housingnavigatorma.org only, starting {data ? fmtDay(data.period.countingFrom) + ', ' + data.period.countingFrom.slice(0, 4) : 'at launch'}{data && data.period.from !== data.period.countingFrom ? ` (showing ${fmtDay(data.period.from)} – ${fmtDay(data.period.to)})` : ''}.</span>
              </p>
            </motion.div>

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700" role="alert">{error}</div>}
            {loading && !data && <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600" /></div>}

            {data && (
              <>
                {empty && (
                  <div className="bg-white rounded-xl shadow-lg p-6 mb-8 text-center text-gray-700" role="status">
                    No conversations yet. Numbers will appear here as renters start using AI Search Mode.
                  </div>
                )}

                <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <StatCard icon={MessagesSquare} title="Total Conversations" value={fmtNum(c.total)} subtitle={`Last ${period} days`} color="blue" />
                  <StatCard icon={Search} title="Listing Searches" value={fmtNum(s.searches)} subtitle={`In ${data.conversationsWithSearch || 0} conversations`} color="green" />
                  <StatCard icon={SearchX} title="Searches With 0 Matches" value={fmtNum(s.zero_match)} subtitle={`${pct(s.zero_match || 0, s.searches || 0)}% of searches, widened instead of a dead end`} color="red" />
                  <StatCard icon={MousePointerClick} title="Listing Clicks" value={fmtNum(listingClicks)} subtitle="Listings and 'See all' opened from answers" color="purple" />
                  <StatCard icon={MessageSquareText} title="Avg Conversation Length" value={c.avgMessages || 0} subtitle="Messages per conversation" color="indigo" />
                  <StatCard icon={ThumbsUp} title="Positive Ratings" value={`${pct(ratings.up, ratings.up + ratings.down)}%`} subtitle={`${ratings.up} up · ${ratings.down} down`} color="orange" />
                  <StatCard icon={Mic} title="Voice Questions" value={fmtNum(voice)} subtitle={`${pct(voice, questions)}% of questions`} color="cyan" />
                  <StatCard icon={Clock} title="Avg Response Time" value={`${c.avgResponseSeconds || 0}s`} subtitle="From question to full answer" color="pink" />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <Panel title="Daily Conversations" icon={MessagesSquare} className="lg:col-span-2" delay={0.5}><DayBars rows={c.perDay} unit="conversations" /></Panel>
                  <Panel title="Conversation Limit" icon={Gauge} delay={0.55}><UsageGauge used={data.thisMonth.used} cap={data.thisMonth.cap} from={data.thisMonth.from} resetsOn={data.thisMonth.resetsOn} /></Panel>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <Panel title="Daily Listing Searches" icon={Search} className="lg:col-span-2" delay={0.6}><DayBars rows={s.perDay} unit="searches" zeroKey="zero_match" /></Panel>
                  <Panel title="How Searches Were Answered" icon={Filter} note="When nothing matched in the place asked, AI Search Mode widens rather than saying there are none" delay={0.65}>
                    <Donut rows={withColors(s.modes, MODES, [GOOD, '#3B82F6', '#F59E0B', BAD])} centerLabel="searches" />
                  </Panel>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">What Renters Search For</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <Panel title="Most Searched Places" icon={MapPin} note="Cities and towns; neighborhoods and zip codes count under their city; red = searches there with 0 matches">
                    <HBars rows={withColors(s.towns)} split="zero_match" splitLabel="with 0 matches" />
                  </Panel>
                  <Panel title="Places With 0 Matches" icon={SearchX} note="No listing matched every filter in that place: demand the inventory doesn't meet">
                    <HBars rows={withColors(s.zeroMatchTowns)} color={BAD} empty="None so far" />
                  </Panel>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <Panel title="Unit Sizes" icon={BedDouble}><Columns rows={beds} /></Panel>
                  <Panel title="Accessibility Needs" icon={Accessibility}><Donut rows={withColors(s.accessibility, ACCESS, ['#8B5CF6', '#06B6D4', '#F97316'])} centerLabel="searches" empty="None asked so far" /></Panel>
                  <Panel title="Availability Asked For" icon={CalendarDays}><HBars rows={withColors(s.availability, AVAIL)} empty="None asked so far" /></Panel>
                  <Panel title="Other Filters" icon={Filter}><HBars rows={withColors(filters)} empty="None asked so far" /></Panel>
                  <Panel title="Asked For, Not a Filter" icon={HelpCircle} note="Requests the search engine can't filter by today">
                    <HBars rows={withColors(s.unsupported, NOT_A_FILTER, ['#F97316', '#F59E0B', '#84CC16', '#06B6D4'])} empty="None so far" />
                  </Panel>
                  <Panel title="From Question to Listing" icon={Funnel} note="Share of conversations reaching each step"><FunnelChart steps={funnel} /></Panel>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Engagement and Questions</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <Panel title="What Renters Did Next" icon={MousePointerClick}><HBars rows={withColors(clicks, CLICKS)} empty="None so far" /></Panel>
                  <Panel title="Question Topics" icon={HelpCircle}><Donut rows={withColors(data.topics, TOPICS)} centerLabel="questions" /></Panel>
                  <Panel title="Help Center Coverage" icon={LifeBuoy} note="Answers where the Help Center had little or nothing on the question: a guide for new content">
                    <Donut rows={[{ label: 'Covered', count: Math.max((help.answers || 0) - (help.notCovered || 0), 0), color: GOOD }, { label: 'Not covered', count: help.notCovered || 0, color: BAD }]}
                      centerValue={`${100 - pct(help.notCovered || 0, help.answers || 0)}%`} centerLabel="covered" />
                  </Panel>
                  <Panel title="Ratings" icon={ThumbsUp}>
                    <Donut rows={[{ label: 'Thumbs up', count: ratings.up, color: GOOD }, { label: 'Thumbs down', count: ratings.down, color: BAD }]}
                      centerValue={`${pct(ratings.up, ratings.up + ratings.down)}%`} centerLabel="positive" empty="No ratings yet" />
                  </Panel>
                  <Panel title="Voice vs Typed" icon={Volume2}>
                    <Donut rows={withColors((data.inputMethods || []).map(x => ({ ...x, name: x.name === 'voice' ? 'Voice' : 'Typed' })), null, ['#3B82F6', '#06B6D4'])} centerLabel="questions" />
                  </Panel>
                  <Panel title="Crisis Resources Shown" icon={LifeBuoy} note="Someone described an emergency and got the crisis resource list (counts only)">
                    <div className="text-3xl font-bold text-gray-900 mb-3">{data.crisis.total}</div>
                    <HBars rows={withColors(data.crisis.byCategory, CRISIS, [BAD, '#F97316', '#F59E0B', '#8B5CF6', '#EC4899'])} empty="None so far" />
                  </Panel>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">Who Uses It, and When</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <Panel title="Language Distribution" icon={Globe}><Donut rows={withColors(data.languages, LANGS)} centerLabel="conversations" wide /></Panel>
                  <Panel title="Devices" icon={Monitor}><Donut rows={withColors(data.devices, DEVICES)} centerLabel="conversations" wide /></Panel>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Panel title="Busiest Hours (Eastern)" icon={Clock} className="lg:col-span-2"><Columns rows={hours} /></Panel>
                  <Panel title="Busiest Days" icon={CalendarDays}><Columns rows={weekdays} color="#10B981" top="#059669" /></Panel>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </motion.div>
  );
}
